import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';

/** One scroll's worth of locations, matching the company picker. */
export const LOCATION_PAGE_SIZE = 50;

interface LocationOption {
  id: string;
  name: string;
}

/**
 * A company's locations, paged lazily as the picker is scrolled.
 *
 * Two endpoints because they are scoped differently: the admin one takes a `companyId` and works
 * across companies, while `getLoVCompanyLocations` serves the caller's own company and is what a
 * customer is permitted to call. The customer endpoint is not paged at all, so it answers in one go
 * and simply reports no more pages.
 */
export const useCompanyLocationOptions = (enabled: boolean, admin: boolean, companyId: string | null) => {
  const { adminGetCompanyLocation, getLoVCompanyLocations } = useContext(ApiContext);

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    (pageNumber: number, append: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      const settle = () => {
        loadingRef.current = false;
        setLoading(false);
      };

      if (admin) {
        if (!companyId) {
          settle();
          return;
        }
        adminGetCompanyLocation(
          companyId,
          // `undefined`, never '': an empty string is sent as `name=` and applied as a filter.
          undefined,
          { pageSize: LOCATION_PAGE_SIZE, pageNumber },
          response => {
            settle();
            pageRef.current = pageNumber;
            setTotal(response.total);
            setLocations(previous => (append ? [...previous, ...response.data] : response.data).map(item => ({ id: item.id, name: item.name })));
          },
          undefined,
          { onError: (_error, showStandardError) => { settle(); showStandardError(); } },
        );
        return;
      }

      getLoVCompanyLocations(
        false,
        data => {
          settle();
          const options = data.map(item => ({ id: item.id, name: item.name }));
          setLocations(options);
          setTotal(options.length);
        },
        undefined,
        { onError: (_error, showStandardError) => { settle(); showStandardError(); } },
      );
    },
    [admin, companyId, adminGetCompanyLocation, getLoVCompanyLocations],
  );

  useEffect(() => {
    pageRef.current = 0;
    setLocations([]);
    setTotal(0);
    if (!enabled) return;
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, admin, companyId]);

  const loadMore = useCallback(() => {
    if (!enabled || loadingRef.current || locations.length >= total) return;
    loadPage(pageRef.current + 1, true);
  }, [enabled, locations.length, total, loadPage]);

  return {
    locations,
    loading,
    hasMore: locations.length < total,
    loadMore,
    options: locations.map(location => ({ label: location.name, value: location.id })),
  };
};